import { useState, useRef, useEffect, useCallback } from "react";
import { Upload, X, FileText, Paperclip } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const MAX_RECEIPT_SIZE = 50 * 1024 * 1024; // 50 MB
const ACCEPTED_EXTENSIONS = [".pdf", ".png", ".jpg", ".jpeg", ".webp"];
const ACCEPTED_MIME = ["application/pdf", "image/png", "image/jpeg", "image/webp"];

function fmtSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/* ── Multi-file interface (new) ── */
interface MultiProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  pasteActive?: boolean;
  multiple?: true;
}

/* ── Single-file interface (legacy compat) ── */
interface SingleProps {
  file: File | null;
  onFileChange: (file: File | null) => void;
  pasteActive?: boolean;
  multiple?: false | undefined;
}

type Props = MultiProps | SingleProps;

function isMulti(p: Props): p is MultiProps {
  return p.multiple === true;
}

export const ReceiptDropZone = (props: Props) => {
  const { pasteActive = false } = props;
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Normalize to array internally
  const files: File[] = isMulti(props) ? props.files : props.file ? [props.file] : [];

  const setFiles = useCallback((next: File[]) => {
    if (isMulti(props)) {
      props.onFilesChange(next);
    } else {
      props.onFileChange(next[0] ?? null);
    }
  }, [props]);

  const validate = useCallback((f: File): boolean => {
    if (f.size > MAX_RECEIPT_SIZE) {
      toast.error(`"${f.name}" is too large — max 50 MB`);
      return false;
    }
    const ext = "." + (f.name.split(".").pop()?.toLowerCase() ?? "");
    if (!ACCEPTED_EXTENSIONS.includes(ext) && !ACCEPTED_MIME.includes(f.type)) {
      toast.error(`"${f.name}" — unsupported file type`);
      return false;
    }
    return true;
  }, []);

  const addFiles = useCallback((incoming: File[]) => {
    const valid = incoming.filter(validate);
    if (valid.length === 0) return;
    if (isMulti(props)) {
      setFiles([...files, ...valid]);
    } else {
      // single mode — take last file
      setFiles([valid[valid.length - 1]]);
    }
  }, [validate, files, setFiles, props]);

  const removeFile = useCallback((idx: number) => {
    setFiles(files.filter((_, i) => i !== idx));
  }, [files, setFiles]);

  // Paste listener
  useEffect(() => {
    if (!pasteActive) return;
    const handler = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const pasted: File[] = [];
      for (const item of Array.from(items)) {
        if (item.kind === "file") {
          const f = item.getAsFile();
          if (f) pasted.push(f);
        }
      }
      if (pasted.length) {
        e.preventDefault();
        addFiles(pasted);
      }
    };
    document.addEventListener("paste", handler);
    return () => document.removeEventListener("paste", handler);
  }, [pasteActive, addFiles]);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length) addFiles(dropped);
  };

  const multiMode = isMulti(props);

  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">
        Receipt{multiMode ? "s" : ""} (optional)
      </label>

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-1.5">
          {files.map((f, i) => (
            <div key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2">
              <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="text-sm truncate flex-1">{f.name}</span>
              <span className="text-xs text-muted-foreground shrink-0">{fmtSize(f.size)}</span>
              <button
                type="button"
                onClick={() => removeFile(i)}
                className="ml-1 rounded-full p-0.5 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Drop zone — always show in multi mode, show in single mode only if no file */}
      {(multiMode || files.length === 0) && (
        <div
          className={cn(
            "relative flex flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed px-4 py-6 cursor-pointer transition-colors",
            dragging
              ? "border-primary bg-primary/5"
              : "border-border hover:border-primary/50 hover:bg-muted/30"
          )}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm text-muted-foreground text-center">
            <span className="font-medium text-foreground">Click</span>, drag & drop, or paste
          </p>
          <p className="text-xs text-muted-foreground">
            PDF, PNG, JPG, WebP — max 50 MB{multiMode ? " each" : ""}
          </p>
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp"
            multiple={multiMode}
            className="hidden"
            onChange={e => {
              const selected = Array.from(e.target.files ?? []);
              if (selected.length) addFiles(selected);
              e.target.value = "";
            }}
          />
        </div>
      )}
    </div>
  );
};
